import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  checkout,
  getEntitlement,
  isTwitchSignInRequiredError,
} from '../api/rttsClient';
import type { Entitlement, StudioCheckoutPlan } from '../api/types';
import { beginTwitchConnect } from '../auth/beginTwitchConnect';
import { setMockAuthState } from '../api/mockState';
import CapReachedCard from '../components/CapReachedCard';
import PlanCards from '../components/PlanCards';
import ReconnectTwitchCard from '../components/ReconnectTwitchCard';
import {
  STUDIO_CHECKOUT_ROUTES,
  STUDIO_COPY,
  STUDIO_DOCUMENT_TITLES,
  defaultCheckoutPlan,
  isStudioBillingEnabled,
  isStudioReelPacksEnabled,
  isStudioUseMock,
} from '../config';
import { useStudioDocumentTitle } from '../hooks/useStudioDocumentTitle';
import { useStudioAuth } from '../hooks/useStudioAuth';

type GateState =
  | { kind: 'loading' }
  | { kind: 'catalog'; entitlement: Entitlement | null }
  | { kind: 'cap_reached'; entitlement: Entitlement }
  | { kind: 'reconnect' }
  | { kind: 'error'; message: string };

const FAQ = [
  { q: STUDIO_COPY.faqEtaQ, a: STUDIO_COPY.faqEtaA },
  { q: STUDIO_COPY.faqWatermarkQ, a: STUDIO_COPY.faqWatermarkA },
  { q: STUDIO_COPY.faqCancelQ, a: STUDIO_COPY.faqCancelA },
] as const;

/**
 * Public pricing / plan page. Billing-off degrades the Pro CTA; it does not hide the catalog.
 */
const StudioUpgradePage: React.FC = () => {
  useStudioDocumentTitle(STUDIO_DOCUMENT_TITLES.upgrade);
  const navigate = useNavigate();
  const { status, isLoggedOut, resolveTimedOut, setMockState, refreshMock } =
    useStudioAuth();
  const [gate, setGate] = useState<GateState>({ kind: 'loading' });
  const [selectedPlan, setSelectedPlan] = useState<Extract<StudioCheckoutPlan, 'monthly' | 'annual'>>(
    defaultCheckoutPlan()
  );
  const [submitting, setSubmitting] = useState(false);
  const [submittingPack, setSubmittingPack] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const billingEnabled = isStudioBillingEnabled();

  const loadGate = useCallback(async () => {
    if (isLoggedOut) {
      setGate({ kind: 'catalog', entitlement: null });
      return;
    }
    setGate({ kind: 'loading' });
    try {
      const entitlement = await getEntitlement();
      if (entitlement.tier === 'paid' && entitlement.period_reels_remaining === 0) {
        setGate({ kind: 'cap_reached', entitlement });
        return;
      }
      setGate({ kind: 'catalog', entitlement });
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setGate({ kind: 'reconnect' });
        return;
      }
      setGate({
        kind: 'error',
        message: STUDIO_COPY.loadAccountFailed,
      });
    }
  }, [isLoggedOut, refreshMock]);

  useEffect(() => {
    if (resolveTimedOut) {
      setGate({ kind: 'catalog', entitlement: null });
      return;
    }
    if (status === 'loading' || status === 'stale_link') return;
    void loadGate();
  }, [status, loadGate, resolveTimedOut]);

  const handleCheckout = async () => {
    if (!billingEnabled || submitting || isLoggedOut) return;
    setCheckoutError(null);
    setSubmitting(true);
    try {
      const { checkout_url } = await checkout(selectedPlan);
      if (isStudioUseMock()) {
        navigate(STUDIO_CHECKOUT_ROUTES.success);
        return;
      }
      window.location.assign(checkout_url);
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setGate({ kind: 'reconnect' });
        return;
      }
      setCheckoutError(STUDIO_COPY.checkoutUnavailable);
      setSubmitting(false);
    }
  };

  const handlePackCheckout = async () => {
    if (!billingEnabled || !isStudioReelPacksEnabled() || submitting || submittingPack || isLoggedOut) {
      return;
    }
    setCheckoutError(null);
    setSubmittingPack(true);
    try {
      const { checkout_url } = await checkout('pack5');
      if (isStudioUseMock()) {
        navigate(`${STUDIO_CHECKOUT_ROUTES.success}?product=pack5`);
        return;
      }
      window.location.assign(checkout_url);
    } catch (error) {
      if (isTwitchSignInRequiredError(error)) {
        if (isStudioUseMock()) {
          setMockAuthState('stale_link');
          refreshMock();
        }
        setGate({ kind: 'reconnect' });
        return;
      }
      setCheckoutError(STUDIO_COPY.checkoutUnavailable);
      setSubmittingPack(false);
    }
  };

  if (
    (status === 'loading' && !resolveTimedOut) ||
    (gate.kind === 'loading' && !resolveTimedOut)
  ) {
    return (
      <div className="flex justify-start py-16" data-testid="studio-upgrade-loading">
        <div className="studio-spinner" />
      </div>
    );
  }

  if (status === 'stale_link' || gate.kind === 'reconnect') {
    return (
      <div>
        <ReconnectTwitchCard
          onReconnect={() => {
            const shouldNavigate = beginTwitchConnect({
              returnTo: '/studio/upgrade',
            });
            if (shouldNavigate) {
              setMockState('linked');
              void loadGate();
            }
          }}
        />
      </div>
    );
  }

  if (gate.kind === 'cap_reached') {
    return (
      <CapReachedCard
        periodCap={gate.entitlement.period_cap}
        periodResetsAt={gate.entitlement.period_resets_at}
      />
    );
  }

  if (gate.kind === 'error') {
    return (
      <div className="studio-panel space-y-4" role="alert">
        <p>{gate.message}</p>
        <button type="button" className="studio-btn-ghost" onClick={() => void loadGate()}>
          {STUDIO_COPY.tryAgain}
        </button>
      </div>
    );
  }

  const pageTestId = billingEnabled
    ? 'studio-upgrade-paywall'
    : 'studio-upgrade-billing-off';

  return (
    <div className="studio-page" data-testid={pageTestId}>
      <header className="studio-section">
        <h1
          className="studio-display text-3xl md:text-4xl"
          data-testid="studio-paywall-headline"
        >
          {STUDIO_COPY.pricingHeadline}
        </h1>
        <p className="studio-page-kicker">{STUDIO_COPY.pricingValueLine}</p>
      </header>

      <PlanCards
        selected={selectedPlan}
        onSelect={setSelectedPlan}
        billingEnabled={billingEnabled}
        isLoggedOut={isLoggedOut}
        submitting={submitting}
        submittingPack={submittingPack}
        onUpgrade={() => void handleCheckout()}
        onPackCheckout={() => void handlePackCheckout()}
      />

      {checkoutError ? (
        <p
          className="text-sm text-[var(--studio-danger)]"
          role="alert"
          data-testid="studio-checkout-error"
        >
          {checkoutError}
        </p>
      ) : null}

      {!billingEnabled ? (
        <p className="studio-fine-print" data-testid="studio-pricing-coming-soon">
          {STUDIO_COPY.pricingComingSoonHint}
        </p>
      ) : (
        <p className="studio-fine-print">{STUDIO_COPY.cancelAnytime}</p>
      )}

      <section className="studio-section" data-testid="studio-pricing-diff">
        <h2 className="studio-page-heading">{STUDIO_COPY.landingDiffHeading}</h2>
        <p className="studio-page-kicker">{STUDIO_COPY.landingDiffSub}</p>
        <ul className="studio-card-grid studio-card-grid--3 studio-landing-diff-list">
          <li className="studio-card">
            <p className="studio-landing-diff-title">
              {STUDIO_COPY.landingDiffReactionTitle}
            </p>
            <p className="studio-landing-card-body">
              {STUDIO_COPY.landingDiffReactionBody}
            </p>
          </li>
          <li className="studio-card">
            <p className="studio-landing-diff-title">
              {STUDIO_COPY.landingDiffOrderTitle}
            </p>
            <p className="studio-landing-card-body">{STUDIO_COPY.landingDiffOrderBody}</p>
          </li>
          <li className="studio-card">
            <p className="studio-landing-diff-title">
              {STUDIO_COPY.landingDiffFootballTitle}
            </p>
            <p className="studio-landing-card-body">
              {STUDIO_COPY.landingDiffFootballBody}
            </p>
          </li>
        </ul>
      </section>

      <section className="studio-section" data-testid="studio-pricing-faq">
        <h2 className="studio-page-heading">{STUDIO_COPY.faqHeading}</h2>
        <div className="studio-card-grid">
          {FAQ.map((item) => (
            <article className="studio-panel" key={item.q}>
              <p className="studio-card-title">{item.q}</p>
              <p className="studio-landing-card-body mt-2">{item.a}</p>
            </article>
          ))}
        </div>
      </section>

      {!isLoggedOut ? (
        <Link to="/studio/vods" className="studio-btn-ghost no-underline inline-flex">
          {STUDIO_COPY.backToVods}
        </Link>
      ) : null}
    </div>
  );
};

export default StudioUpgradePage;
